/**
 * Elbow evidence — the engine-side half of the 'limb-axis' recentre: gather
 * the arm's BIND-pose world vertices and hand them to the kernel's pure
 * `limbAxisCentre`. The kernel decides; this only fetches.
 *
 * Vertices are transformed by the mesh's stored `bindMatrix` ONLY — the
 * pose-independent bind-world frame, the same frame IBM-derived joint
 * positions live in. The earlier `applyBoneTransform` + `localToWorld` path
 * was bind-equal only while the bones actually SAT at bind: measured
 * 2026-08-02 (fox, hands panel), a PLAYING clip put the sampled flesh
 * mid-stride while the joint stayed in bind space, and the capture found
 * zero vertices. A mesh presented under any pose now still measures its own
 * rest flesh, and a drifted scene TRS cannot poison the measurement either
 * (v2's live-TRS lesson, ADR-008 §2b — bindMatrix is the world at bind).
 *
 * The axis is the WHOLE arm line (shoulder → hand), not the forearm alone:
 * at bind the arm is straight, and the long baseline is far less sensitive to
 * a few millimetres of joint placement than the short forearm segment would
 * be — the thing we are trying to measure must not define its own axis.
 *
 * Depends on: three, kernel (limbAxisCentre). Depended on by: the elbow
 * proposal in the Hands/Character panel and the recentre door.
 */
import { Vector3, type Object3D, type SkinnedMesh } from 'three';
import { limbAxisCentre, type LimbAxisResult, type RigDescriptor, type Vec3Data } from '@harryhausen/kernel';

export interface ElbowEvidence extends LimbAxisResult {
  /** The rig bone measured — the elbow joint itself (the forearm's origin). */
  readonly bone: string;
  /** Where that joint sits now, in world bind space. */
  readonly jointWorld: Vec3Data;
  /** Forearm length, the scale every number here is relative to. */
  readonly forearmLength: number;
  /** Offset as a percentage of the forearm — the comparable figure across rigs. */
  readonly offsetFraction: number;
}

/** Every skinned vertex in the subtree, in BIND-pose world space. */
export function bindPoseVertices(root: Object3D): Vec3Data[] {
  const out: Vec3Data[] = [];
  const v = new Vector3();
  root.traverse((node) => {
    const mesh = node as SkinnedMesh;
    if (mesh.isSkinnedMesh !== true) return;
    const position = mesh.geometry.getAttribute('position');
    for (let i = 0; i < position.count; i += 1) {
      v.fromBufferAttribute(position, i);
      v.applyMatrix4(mesh.bindMatrix);
      out.push({ x: v.x, y: v.y, z: v.z });
    }
  });
  return out;
}

/**
 * Measure one elbow. `upper`/`elbow`/`hand` are ONTOLOGY ids; the rig's own
 * names are resolved through its mapping, so a rig that does not map the arm
 * is a miss the caller can report rather than a crash.
 */
export function measureElbow(
  root: Object3D,
  rig: RigDescriptor,
  side: 'Left' | 'Right',
  vertices?: readonly Vec3Data[],
): ElbowEvidence | null {
  const boneFor = (id: string): string | undefined => rig.mapping.find((m) => m.ontologyId === id)?.rigBone;
  const upperName = boneFor(`${side}Arm`);
  const elbowName = boneFor(`${side}ForeArm`);
  const handName = boneFor(`${side}Hand`);
  if (upperName === undefined || elbowName === undefined || handName === undefined) return null;
  const bone = (n: string): { x: number; y: number; z: number } | null =>
    rig.bones.find((b) => b.name === n)?.worldBind.position ?? null;
  const upper = bone(upperName);
  const elbow = bone(elbowName);
  const hand = bone(handName);
  if (upper === null || elbow === null || hand === null) return null;

  const axis = { x: hand.x - upper.x, y: hand.y - upper.y, z: hand.z - upper.z };
  const forearmLength = Math.hypot(hand.x - elbow.x, hand.y - elbow.y, hand.z - elbow.z);
  const verts = vertices ?? bindPoseVertices(root);
  const measured = limbAxisCentre(verts, elbow, axis, forearmLength);
  return {
    ...measured,
    bone: elbowName,
    jointWorld: elbow,
    forearmLength,
    offsetFraction: measured.offset / forearmLength,
  };
}
